import { useEffect, useState } from 'react'
import { makeStyles, tokens } from '@fluentui/react-components'
import { openDemandInNewTab } from '../lib/demandNav'

/** Position + target of an open-in-new-tab context menu, or null when closed. */
export type DemandContextMenuState = { id: number; x: number; y: number } | null

/**
 * Manages the right-click "Open in new tab" menu for demand rows/cards:
 * tracks the target demand + pointer position and closes the menu on any
 * outside click or Escape. Shared by DemandList (table rows) and
 * DemandKanban (cards) so the two views behave identically (issue #838).
 */
export function useOpenInNewTabMenu() {
  const [menu, setMenu] = useState<DemandContextMenuState>(null)

  useEffect(() => {
    if (!menu) return

    const close = () => setMenu(null)
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMenu(null)
    }

    window.addEventListener('click', close)
    window.addEventListener('keydown', closeOnEscape)
    return () => {
      window.removeEventListener('click', close)
      window.removeEventListener('keydown', closeOnEscape)
    }
  }, [menu])

  /** Open the menu at the pointer, from a right-click (context-menu) event. */
  const openMenu = (
    id: number,
    event: { preventDefault: () => void; clientX: number; clientY: number },
  ) => {
    event.preventDefault()
    setMenu({ id, x: event.clientX, y: event.clientY })
  }
  const closeMenu = () => setMenu(null)

  return { menu, openMenu, closeMenu }
}

const useStyles = makeStyles({
  contextMenu: {
    position: 'fixed',
    zIndex: 1000,
    minWidth: '180px',
    paddingTop: tokens.spacingVerticalXXS,
    paddingRight: tokens.spacingHorizontalXXS,
    paddingBottom: tokens.spacingVerticalXXS,
    paddingLeft: tokens.spacingHorizontalXXS,
    background: tokens.colorNeutralBackground1,
    borderTopWidth: '1px',
    borderRightWidth: '1px',
    borderBottomWidth: '1px',
    borderLeftWidth: '1px',
    borderTopStyle: 'solid',
    borderRightStyle: 'solid',
    borderBottomStyle: 'solid',
    borderLeftStyle: 'solid',
    borderTopColor: tokens.colorNeutralStroke2,
    borderRightColor: tokens.colorNeutralStroke2,
    borderBottomColor: tokens.colorNeutralStroke2,
    borderLeftColor: tokens.colorNeutralStroke2,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow16,
  },
  contextMenuItem: {
    width: '100%',
    border: '0',
    background: 'transparent',
    paddingTop: tokens.spacingVerticalS,
    paddingRight: tokens.spacingHorizontalM,
    paddingBottom: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    textAlign: 'left',
    borderRadius: tokens.borderRadiusSmall,
    cursor: 'pointer',
    color: tokens.colorNeutralForeground1,
    ':hover': {
      background: tokens.colorNeutralBackground1Hover,
    },
  },
})

/**
 * Floating "Open in new tab" menu, rendered at the cursor when `menu` is set
 * and nothing when it's null. `onClose` should clear the menu state. The
 * `onClick` stopPropagation keeps the hook's window-level close handler from
 * firing when the user clicks inside the menu itself.
 */
export function OpenInNewTabMenu({
  menu,
  onClose,
  testId = 'demand-open-context-menu',
}: {
  menu: DemandContextMenuState
  onClose: () => void
  testId?: string
}) {
  const styles = useStyles()
  if (!menu) return null
  return (
    <div
      className={styles.contextMenu}
      role="menu"
      data-testid={testId}
      style={{ top: menu.y, left: menu.x }}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className={styles.contextMenuItem}
        role="menuitem"
        onClick={() => {
          openDemandInNewTab(menu.id)
          onClose()
        }}
      >
        Open in new tab
      </button>
    </div>
  )
}
